import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, User, Bell, Shield, Palette, Save, Loader2, Package, Monitor, Copy } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { fr } from "../../locales/fr"
import { getGymDisplaySettings, regenerateDisplayToken, getGymAgentSettings, regenerateAgentToken } from "../../services/accessService"
import { getGymProfile, updateGymProfile } from "../../services/gymService"

const tabs = [
  { id: "gym", label: fr.settings.tabs.gym, icon: Building2 },
  { id: "account", label: fr.settings.tabs.account, icon: User },
  { id: "notifications", label: fr.settings.tabs.notifications, icon: Bell },
  { id: "security", label: fr.settings.tabs.security, icon: Shield },
  { id: "appearance", label: fr.settings.tabs.appearance, icon: Palette },
]

export default function Settings() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("gym")
  const [saving, setSaving] = useState(false)
  const [gymData, setGymData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
  })
  const [gymLoad, setGymLoad] = useState(false)
  const [gymSaveError, setGymSaveError] = useState("")
  const [displayCfg, setDisplayCfg] = useState(null)
  const [displayLoad, setDisplayLoad] = useState(false)
  const [displaySaving, setDisplaySaving] = useState(false)
  const [agentCfg, setAgentCfg] = useState(null)
  const [agentLoad, setAgentLoad] = useState(false)
  const [agentSaving, setAgentSaving] = useState(false)
  const [agentError, setAgentError] = useState("")

  useEffect(() => {
    if (activeTab !== "gym") return
    let cancelled = false
    const run = async () => {
      setDisplayLoad(true)
      setAgentLoad(true)
      setGymLoad(true)
      setAgentError("")
      setGymSaveError("")
      try {
        const [profile, d, a] = await Promise.all([
          getGymProfile(),
          getGymDisplaySettings(),
          getGymAgentSettings().catch((e) => {
            const msg =
              e?.response?.data?.detail ||
              e?.response?.data?.message ||
              e?.message ||
              "Erreur chargement jeton agent"
            if (!cancelled) setAgentError(String(msg))
            return null
          }),
        ])
        if (!cancelled) {
          setGymData({
            name: profile?.name || "",
            email: profile?.email || "",
            phone: profile?.phone || "",
            address: profile?.address || "",
            city: profile?.city || "",
          })
          setDisplayCfg(d)
          if (a) setAgentCfg(a)
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (!cancelled) {
          setDisplayLoad(false)
          setAgentLoad(false)
          setGymLoad(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [activeTab])

  const entranceUrl =
    displayCfg?.displayToken != null
      ? `${window.location.origin}/entrance-display?token=${encodeURIComponent(displayCfg.displayToken)}`
      : ""

  const handleRegenerateDisplayToken = async () => {
    if (!window.confirm(fr.settings.gym.displayRegenerateConfirm)) return
    setDisplaySaving(true)
    try {
      const data = await regenerateDisplayToken()
      setDisplayCfg((prev) =>
        prev ? { ...prev, displayToken: data.displayToken } : { displayToken: data.displayToken, gymName: "", gymId: 0 }
      )
    } catch (e) {
      console.error(e)
    }
    setDisplaySaving(false)
  }

  const handleRegenerateAgentToken = async () => {
    if (!window.confirm(fr.settings.gym.agentRegenerateConfirm)) return
    setAgentSaving(true)
    setAgentError("")
    try {
      const data = await regenerateAgentToken()
      setAgentCfg((prev) =>
        prev
          ? { ...prev, agentSyncToken: data.agentSyncToken, boundFingerprint: null }
          : { agentSyncToken: data.agentSyncToken, gymName: "", gymId: 0 }
      )
    } catch (e) {
      console.error(e)
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Échec régénération jeton agent"
      setAgentError(String(msg))
    }
    setAgentSaving(false)
  }

  const agentStatusLabel = (status) => {
    const map = {
      active: "Actif",
      suspended: "Suspendu",
      revoked: "Révoqué",
      expired: "Expiré",
    }
    return map[status] || status || "—"
  }

  const handleSave = async () => {
    if (activeTab === "gym") {
      setSaving(true)
      setGymSaveError("")
      try {
        await updateGymProfile({
          name: gymData.name.trim(),
          email: gymData.email.trim() || null,
          phone: gymData.phone.trim() || null,
          address: gymData.address.trim() || null,
          city: gymData.city.trim() || null,
        })
      } catch (e) {
        console.error(e)
        setGymSaveError(e?.response?.data?.error || fr.settings.gym.saveError)
      }
      setSaving(false)
      return
    }
    setSaving(true)
    await new Promise((resolve) => setTimeout(resolve, 500))
    setSaving(false)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{fr.settings.title}</h1>
        <p className="text-muted-foreground">{fr.settings.subtitle}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <Card className="h-fit lg:w-64">
          <CardContent className="p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                    ${activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }
                  `}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        <div className="flex-1">
          {activeTab === "gym" && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.settings.gym.title}</CardTitle>
                <CardDescription>{fr.settings.gym.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {gymLoad ? (
                  <p className="text-sm text-muted-foreground">{fr.common.loading}</p>
                ) : (
                  <>
                <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{fr.gyms.manageLink}</p>
                    <p className="text-sm text-muted-foreground">{fr.gyms.subtitle}</p>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => navigate("/settings/gyms")}>
                    {fr.settings.gym.manageGymsLink}
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.gym.gymName}
                    </label>
                    <Input
                      value={gymData.name}
                      onChange={(e) => setGymData({ ...gymData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.gym.email}
                    </label>
                    <Input
                      type="email"
                      value={gymData.email}
                      onChange={(e) => setGymData({ ...gymData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.gym.phone}
                    </label>
                    <Input
                      value={gymData.phone}
                      onChange={(e) => setGymData({ ...gymData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.gym.city}
                    </label>
                    <Input
                      value={gymData.city}
                      onChange={(e) => setGymData({ ...gymData, city: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {fr.settings.gym.address}
                  </label>
                  <Input
                    value={gymData.address}
                    onChange={(e) => setGymData({ ...gymData, address: e.target.value })}
                  />
                </div>
                  </>
                )}

                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <Monitor className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="font-medium text-foreground">{fr.settings.gym.displayScreenTitle}</p>
                      <p className="text-sm text-muted-foreground">{fr.settings.gym.displayScreenDescription}</p>
                      {displayLoad ? (
                        <p className="text-sm text-muted-foreground">{fr.settings.gym.displayLoading}</p>
                      ) : (
                        <>
                          <label className="text-xs font-medium text-muted-foreground">
                            {fr.settings.gym.displayUrlLabel}
                          </label>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <Input readOnly value={entranceUrl} className="font-mono text-xs sm:flex-1" />
                            <div className="flex shrink-0 gap-2">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => void navigator.clipboard.writeText(entranceUrl)}
                                disabled={!entranceUrl}
                              >
                                <Copy className="mr-2 h-4 w-4" />
                                {fr.settings.gym.displayCopy}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => void handleRegenerateDisplayToken()}
                                disabled={displaySaving || !displayCfg}
                              >
                                {displaySaving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  fr.settings.gym.displayRegenerate
                                )}
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{fr.settings.gym.displayTokenHint}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <Shield className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 space-y-3">
                      <div>
                        <p className="font-medium text-foreground">{fr.settings.gym.agentTitle}</p>
                        <p className="text-sm text-muted-foreground">{fr.settings.gym.agentDescription}</p>
                      </div>
                      {agentLoad ? (
                        <p className="text-sm text-muted-foreground">{fr.settings.gym.agentLoading}</p>
                      ) : (
                        <>
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {fr.settings.gym.agentApiUrl}
                            </label>
                            <Input
                              readOnly
                              value={agentCfg?.apiBaseUrl || window.location.origin}
                              className="font-mono text-xs"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {fr.settings.gym.agentTokenLabel}
                            </label>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <Input
                                readOnly
                                value={agentCfg?.agentSyncToken || ""}
                                className="font-mono text-xs sm:flex-1"
                              />
                              <div className="flex shrink-0 gap-2">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() =>
                                    void navigator.clipboard.writeText(agentCfg?.agentSyncToken || "")
                                  }
                                  disabled={!agentCfg?.agentSyncToken}
                                >
                                  <Copy className="mr-2 h-4 w-4" />
                                  {fr.settings.gym.agentCopy}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => void handleRegenerateAgentToken()}
                                  disabled={agentSaving}
                                >
                                  {agentSaving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    fr.settings.gym.agentRegenerate
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                          {agentError ? (
                            <p className="text-sm text-destructive">{agentError}</p>
                          ) : null}
                          <div className="rounded-md border border-border/60 bg-background/40 p-3 text-sm">
                            <p className="mb-2 font-medium text-foreground">
                              {fr.settings.gym.agentLicenseTitle}
                            </p>
                            <dl className="grid gap-1 text-muted-foreground sm:grid-cols-[9rem_1fr]">
                              <dt>{fr.settings.gym.agentStatus}</dt>
                              <dd className="text-foreground">
                                {agentStatusLabel(agentCfg?.agentStatus || "active")}
                              </dd>
                              <dt>{fr.settings.gym.agentLastSeen}</dt>
                              <dd className="text-foreground">
                                {agentCfg?.agentLastSeenAt
                                  ? new Date(agentCfg.agentLastSeenAt).toLocaleString()
                                  : fr.settings.gym.agentLastSeenNever}
                              </dd>
                              <dt>{fr.settings.gym.agentBoundPc}</dt>
                              <dd className="break-all font-mono text-xs text-foreground">
                                {agentCfg?.boundFingerprint || fr.settings.gym.agentBoundNone}
                              </dd>
                            </dl>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <Package className="h-5 w-5" aria-hidden />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{fr.settings.packs.title}</p>
                      <p className="text-sm text-muted-foreground">{fr.settings.packs.manageDescription}</p>
                    </div>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => navigate("/settings/packs")}>
                    {fr.settings.packs.manageLink}
                  </Button>
                </div>

                {gymSaveError && (
                  <p className="text-sm text-destructive">{gymSaveError}</p>
                )}

                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving || gymLoad}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {fr.common.saving}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {fr.settings.gym.saveChanges}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === "account" && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.settings.account.title}</CardTitle>
                <CardDescription>{fr.settings.account.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.account.fullName}
                    </label>
                    <Input defaultValue="Admin User" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.account.email}
                    </label>
                    <Input type="email" defaultValue="admin@gymflow.com" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {fr.common.saving}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {fr.settings.gym.saveChanges}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === "notifications" && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.settings.notifications.title}</CardTitle>
                <CardDescription>{fr.settings.notifications.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {fr.settings.notifications.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between border-b border-border py-3 last:border-0"
                  >
                    <div>
                      <p className="font-medium text-foreground">{item.label}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input type="checkbox" defaultChecked className="peer sr-only" />
                      <div className="h-6 w-11 rounded-full bg-muted peer-checked:bg-primary after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                    </label>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {activeTab === "security" && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.settings.security.title}</CardTitle>
                <CardDescription>{fr.settings.security.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.security.currentPassword}
                    </label>
                    <Input type="password" placeholder={fr.settings.security.currentPlaceholder} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.security.newPassword}
                    </label>
                    <Input type="password" placeholder={fr.settings.security.newPlaceholder} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {fr.settings.security.confirmPassword}
                    </label>
                    <Input type="password" placeholder={fr.settings.security.confirmPlaceholder} />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {fr.common.updating}
                      </>
                    ) : (
                      fr.settings.security.updatePassword
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === "appearance" && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.settings.appearance.title}</CardTitle>
                <CardDescription>{fr.settings.appearance.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="mb-3 font-medium text-foreground">{fr.settings.appearance.theme}</p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="flex flex-col items-center gap-2 rounded-lg border-2 border-primary p-4"
                      >
                        <div className="h-8 w-8 rounded-full bg-foreground" />
                        <span className="text-sm text-foreground">{fr.settings.appearance.dark}</span>
                      </button>
                      <button
                        type="button"
                        className="flex flex-col items-center gap-2 rounded-lg border border-border p-4 opacity-50"
                      >
                        <div className="h-8 w-8 rounded-full bg-muted" />
                        <span className="text-sm text-muted-foreground">
                          {fr.settings.appearance.light}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
